import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';

type ContentDisplayItem = CollectionPreferencesProps.ContentDisplayItem;

/**
 * Reconciles a (possibly stale) Cloudscape `contentDisplay` against the current set of
 * selected field values so that newly-enabled fields always surface as visible columns.
 *
 * The custom datasource column-preferences state can lag behind the field selection: a
 * field enabled after `contentDisplay` was populated is absent from it, which both hides
 * the column (Cloudscape only renders ids present in `columnDisplay`) and shows the field
 * as toggled-off in the preferences modal (RSP-6056).
 *
 * - `undefined` in → `undefined` out: no preferences set, so Cloudscape shows all columns.
 * - Entries whose id no longer maps to a selected field are dropped.
 * - The existing order and visibility of known fields are preserved.
 * - Any selected field missing from `contentDisplay` is appended as visible.
 *
 * @param contentDisplay the current preferences `contentDisplay`, or `undefined`
 * @param fieldValues the unique ids (`${dataSourceIndex}|${fieldId}`) of the currently selected fields, in order
 */
export const reconcileContentDisplay = (
  contentDisplay: readonly ContentDisplayItem[] | undefined,
  fieldValues: string[]
): ContentDisplayItem[] | undefined => {
  if (!contentDisplay) {
    return undefined;
  }

  const validIds = new Set(fieldValues);
  const kept = contentDisplay.filter((item) => validIds.has(item.id));
  const keptIds = new Set(kept.map((item) => item.id));
  const appended: ContentDisplayItem[] = fieldValues
    .filter((id) => !keptIds.has(id))
    .map((id) => ({ id, visible: true }));

  return [...kept, ...appended];
};
